import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import styled from 'styled-components';

import { ProbabilisticBundleReward } from '@sorare/core/src/components/3d/ProbabilisticBundleReward';
import type { StuffInRewardBoxType } from '@sorare/core/src/components/rewards/RewardBoxDialog/types';
import { useIntlContext } from '@sorare/core/src/contexts/intl';
import { isType } from '@sorare/core/src/lib/gql';
import { getInGameCurrencyDescription } from '@sorare/core/src/lib/inGameCurrencyDescription';
import { lazy } from '@sorare/core/src/lib/retry';

import type {
  InGameCurrencyInRewardBox_ProbabilisticBundleSlotItemInterface,
  InGameCurrencyInRewardBox_ProbabilisticBundleSlotItemInterface_ProbabilisticBundleSlotInGameCurrencyItem_,
} from './__generated__/index.graphql';

const ItemInRewardBox = lazy(
  async () => import('@sorare/trois/src/components/RewardBox/ItemInRewardBox'),
  'ItemInRewardBox'
);

const NoWrap = styled.div`
  white-space: nowrap;
`;

type Props = {
  item: InGameCurrencyInRewardBox_ProbabilisticBundleSlotItemInterface;
  visible: boolean;
};

const InGameCurrencyInRewardBoxCanHandle = (
  item: Props['item']
): item is InGameCurrencyInRewardBox_ProbabilisticBundleSlotItemInterface_ProbabilisticBundleSlotInGameCurrencyItem_ => {
  return isType(item, 'ProbabilisticBundleSlotInGameCurrencyItem');
};

export const InGameCurrencyInRewardBox: StuffInRewardBoxType<
  InGameCurrencyInRewardBox_ProbabilisticBundleSlotItemInterface
> = ({ item, visible }: Props) => {
  const { formatMessage } = useIntlContext();
  if (!InGameCurrencyInRewardBoxCanHandle(item)) {
    return null;
  }

  return (
    <ItemInRewardBox
      visible={visible}
      description={
        <NoWrap>{getInGameCurrencyDescription(item, formatMessage)}</NoWrap>
      }
    >
      <ProbabilisticBundleReward item={item} />
    </ItemInRewardBox>
  );
};

InGameCurrencyInRewardBox.fragments = {
  ProbabilisticBundleSlotItemInterface: gql`
    fragment InGameCurrencyInRewardBox_ProbabilisticBundleSlotItemInterface on ProbabilisticBundleSlotItemInterface {
      id
      ...ProbabilisticBundleReward_probabilisticBundleSlotItemInterface
      ... on ProbabilisticBundleSlotInGameCurrencyItem {
        id
        currency
        amount
      }
      ...getInGameCurrencyDescription_probabilisticBundleSlotItemInterface
    }
    ${ProbabilisticBundleReward.fragments.probabilisticBundleSlotItemInterface}
    ${getInGameCurrencyDescription.fragments
      .probabilisticBundleSlotItemInterface}
  ` as TypedDocumentNode<InGameCurrencyInRewardBox_ProbabilisticBundleSlotItemInterface>,
};

const InGameCurrencyInRewardBoxPreloader = () => {
  return null;
};

InGameCurrencyInRewardBox.preloader = InGameCurrencyInRewardBoxPreloader;

InGameCurrencyInRewardBox.canHandle = InGameCurrencyInRewardBoxCanHandle;

InGameCurrencyInRewardBox.fallback = (item: Props['item']) => {
  if (!InGameCurrencyInRewardBoxCanHandle(item)) {
    return null;
  }
  return <div />;
};

InGameCurrencyInRewardBox.description = getInGameCurrencyDescription;
